import { useNotes } from '@/hooks/useNotes';
import { NoteSummary } from '@common/schemas/notes.schema';
import type { EventSummary } from '@/lib/types';
import { AccessTime, Autorenew, Download, FolderOutlined } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { EntityPickerMenu } from '@/components/editor/EntityPickerMenu';
import { useNovel } from '@/context/novel-context';
import { useNarrativeItem } from '@/hooks/use-entity';
import { Note } from '@common/schemas/notes.schema';
import { Check, Close } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

interface NoteDetailsPanelProps {
  noteId: string;
  noteSummary: NoteSummary;
  onUpdateDraftFolder?: (folderId: string | null) => void;
  onUpdateDraftTags?: (tags: string[]) => void;
  onUpdateDraftLinkedEntities?: (entityIds: string[]) => void;
}

export function NoteDetailsPanel({
  noteId,
  noteSummary,
  onUpdateDraftFolder,
  onUpdateDraftTags,
  onUpdateDraftLinkedEntities
}: NoteDetailsPanelProps) {
  const { t } = useTranslation();
  const { updateNote } = useNotes();
  const { novel } = useNovel();

  const { data: actualNote } = useNarrativeItem<Note>('notes', noteId === 'draft' ? undefined : noteId);

  // Use noteSummary for drafts, otherwise strictly use actualNote
  const currentNote = noteId === 'draft' ? noteSummary : (actualNote || noteSummary);

  const [isEditingTags, setIsEditingTags] = useState(false);
  const [tagsInput, setTagsInput] = useState('');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const folderParents = novel?.noteFolderParents || {};
  const getFolderDepth = (id: string) => { let depth = 0; let parent = folderParents[id]; const seen = new Set<string>(); while (parent && !seen.has(parent)) { seen.add(parent); depth += 1; parent = folderParents[parent]; } return depth; };
  const folderEntries = Object.entries(novel?.noteFolders || {}).map(([id, name]) => ({ id, name, depth: getFolderDepth(id) }));
  const folders: typeof folderEntries = [];
  const appendFolderTree = (parentId?: string) => {
    folderEntries.filter((folder) => (folderParents[folder.id] || undefined) === parentId).sort((a, b) => a.name.localeCompare(b.name)).forEach((folder) => {
      folders.push(folder);
      appendFolderTree(folder.id);
    });
  };
  appendFolderTree();
  folderEntries.filter((folder) => !folders.some((item) => item.id === folder.id)).sort((a, b) => a.name.localeCompare(b.name)).forEach((folder) => { folders.push(folder); appendFolderTree(folder.id); });

  const noteUpdatedAt = 'audit' in currentNote ? currentNote.audit.updatedAt : currentNote.updatedAt;
  const noteCreatedAt = 'audit' in currentNote ? currentNote.audit.createdAt : (currentNote.createdAt || currentNote.updatedAt);
  const formatDate = (value: string | undefined, options: Intl.DateTimeFormatOptions) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString(undefined, options);
  };

  const createdDate = formatDate(noteCreatedAt, {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  const updatedDate = formatDate(noteUpdatedAt, {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
  // Combine all entities to show in the picker
  const allPickableEntities = useMemo(() => {
    if (!novel?.summaries) return [];

    const chars = Object.values(novel.summaries.characters || {}).map(c => ({
      id: c.id,
      name: c.name || 'Unknown',
      category: t('sidebar.characters'),
      entityType: 'characters'
    }));
    const locs = Object.values(novel.summaries.locations || {}).map(l => ({
      id: l.id,
      name: l.name || 'Unknown',
      category: t('sidebar.locations'),
      entityType: 'locations'
    }));
    const orgs = Object.values(novel.summaries.organizations || {}).map(o => ({
      id: o.id,
      name: o.name || 'Unknown',
      category: t('sidebar.organizations'),
      entityType: 'organizations'
    }));
    const codex = Object.values(novel.summaries.codex || {}).map(i => ({
      id: i.id,
      name: i.name || 'Unknown',
      category: novel.codexCategories?.[i.type] || t('sidebar.codex'),
      entityType: 'codex'
    }));
    const events = (Object.values(novel.summaries.events || {}) as EventSummary[])
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(e => ({
        id: e.id,
      name: e.name || 'Unknown',
      category: t('sidebar.events'),
      entityType: 'events'
    }));

    return [...chars, ...locs, ...orgs, ...codex, ...events];
  }, [novel?.summaries, novel?.codexCategories, t]);

  const navigate = useNavigate();

  const linkedEntityIds = currentNote?.linkedEntityIds || [];

  // Linked items currently set
  const linkedItems = useMemo(() => {
    return linkedEntityIds.map(id => {
      const match = allPickableEntities.find(e => e.id === id);
      return match ? match : { id, name: t('research_notes.note_details.unknown_entity'), entityType: '' };
    });
  }, [linkedEntityIds, allPickableEntities, t]);

  // Items not yet linked (for the picker)
  const availableItemsToLink = useMemo(() => {
    let filtered = allPickableEntities.filter(e => !linkedEntityIds.includes(e.id));
    if (searchQuery) {
      filtered = filtered.filter(e => e.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return filtered;
  }, [allPickableEntities, linkedEntityIds, searchQuery]);

  const handleLinkEntity = useCallback((entityId: string) => {
    const newLinked = [...linkedEntityIds, entityId];
    if (noteId === 'draft' && onUpdateDraftLinkedEntities) {
      onUpdateDraftLinkedEntities(newLinked);
    } else {
      updateNote(noteId, { linkedEntityIds: newLinked });
    }
  }, [noteId, linkedEntityIds, onUpdateDraftLinkedEntities, updateNote]);

  const handleUnlinkEntity = useCallback((entityId: string) => {
    const newLinked = linkedEntityIds.filter(id => id !== entityId);
    if (noteId === 'draft' && onUpdateDraftLinkedEntities) {
      onUpdateDraftLinkedEntities(newLinked);
    } else {
      updateNote(noteId, { linkedEntityIds: newLinked });
    }
  }, [noteId, linkedEntityIds, onUpdateDraftLinkedEntities, updateNote]);

  const handleEntityClick = useCallback((entityType: string, id: string) => {
    switch (entityType) {
      case 'characters':
        navigate({ to: '/characters/view/$id', params: { id } });
        break;
      case 'locations':
        navigate({ to: '/locations/view/$id', params: { id } });
        break;
      case 'organizations':
        navigate({ to: '/organizations/view/$id', params: { id } });
        break;
      case 'codex':
        navigate({ to: '/codex/view/$id', params: { id } });
        break;
      case 'events':
        navigate({ to: '/events/edit/$id', params: { id } });
        break;
    }
  }, [navigate]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t('research_notes.note_details.title')}
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Properties */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
            <FolderOutlined fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('research_notes.note_details.folder')}</Typography>
          </Box>
          <Box sx={{ pl: 3 }}>
            <Select
              size="small"
              fullWidth
              value={currentNote?.folderId || 'none'}
              onChange={(e) => {
                const newFolderId = e.target.value === 'none' ? null : e.target.value;
                if (noteId === 'draft' && onUpdateDraftFolder) {
                  onUpdateDraftFolder(newFolderId);
                } else {
                  updateNote(noteId, { folderId: newFolderId });
                }
              }}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="none">
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  {t('research_notes.collections.unsorted')}
                </Typography>
              </MenuItem>
              {folders.map(folder => (
                <MenuItem key={folder.id} value={folder.id}>
                  <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: 28 }}>
                    {Array.from({ length: folder.depth }).map((_, index) => <Box key={index} sx={{ width: 6, borderLeft: '2px solid', borderColor: 'primary.light', mr: 0.5 }} />)}
                    <Box>{folder.name}</Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
            <Autorenew fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('research_notes.note_details.last_updated')}</Typography>
          </Box>
          <Typography variant="body2" sx={{ pl: 3 }}>{updatedDate}</Typography>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
            <AccessTime fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('research_notes.note_details.created')}</Typography>
          </Box>
          <Typography variant="body2" sx={{ pl: 3 }}>{createdDate}</Typography>
        </Box>

        {currentNote?.sourceImportSessionId && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
              <Download fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('research_notes.note_details.source')}</Typography>
            </Box>
            <Typography variant="body2" sx={{ pl: 3, color: 'primary.main' }}>
              {t('research_notes.note_details.imported_source')}
            </Typography>
          </Box>
        )}

        <Divider />

        {/* Tags */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {t('research_notes.note_details.tags')}
            </Typography>
            {!isEditingTags && (
              <Link
                component="button"
                variant="caption"
                sx={{ fontWeight: 600 }}
                onClick={() => {
                  setTagsInput(currentNote?.tags?.join(', ') || '');
                  setIsEditingTags(true);
                }}
              >
                {t('research_notes.note_details.edit')}
              </Link>
            )}
          </Box>

          {isEditingTags ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="tag1, tag2, tag3..."
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const newTags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
                    if (noteId === 'draft' && onUpdateDraftTags) {
                      onUpdateDraftTags(newTags);
                    } else {
                      updateNote(noteId, { tags: newTags });
                    }
                    setIsEditingTags(false);
                  }
                }}
              />
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  const newTags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
                  if (noteId === 'draft' && onUpdateDraftTags) {
                    onUpdateDraftTags(newTags);
                  } else {
                    updateNote(noteId, { tags: newTags });
                  }
                  setIsEditingTags(false);
                }}
              >
                <Check fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setIsEditingTags(false)}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {currentNote?.tags && currentNote.tags.length > 0 ? (
                currentNote.tags.map(tag => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('research_notes.note_details.no_tags')}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Divider />

        {/* Linked Items */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {t('research_notes.note_details.linked_items')}
            </Typography>
            <Link
              component="button"
              variant="caption"
              sx={{ fontWeight: 600 }}
              onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
            >
              {t('research_notes.note_details.link_entity')}
            </Link>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {linkedItems.length > 0 ? (
              linkedItems.map(item => (
                <Chip
                  key={item.id}
                  label={item.name}
                  size="small"
                  variant="outlined"
                  onClick={() => item.entityType ? handleEntityClick(item.entityType, item.id) : null}
                  onDelete={() => handleUnlinkEntity(item.id)}
                  sx={{ cursor: item.entityType ? 'pointer' : 'default' }}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('research_notes.note_details.no_links')}
              </Typography>
            )}
          </Box>

          <EntityPickerMenu
            anchorEl={anchorEl}
            items={availableItemsToLink}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSelect={handleLinkEntity}
            onClose={() => {
              setAnchorEl(null);
              setSearchQuery('');
            }}
          />
        </Box>

      </Box>
    </Box>
  );
}
